import React, { useEffect, useState } from "react";
import { Plus, Laptop } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Badge, Loading, Modal, EmptyState } from "../components/ui";

const TYPES = ["LAPTOP", "DESKTOP", "MOBILE", "MONITOR", "FURNITURE", "OTHER"];

export default function Assets() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [emps, setEmps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "LAPTOP", serialNumber: "" });
  const canManage = ["SUPERADMIN", "HR_ADMIN"].includes(user?.role);

  const load = () => api.get("/assets").then(({ data }) => setItems(data));
  useEffect(() => { load(); api.get("/employees?limit=100").then(({ data }) => setEmps(data.items)); }, []);
  const add = async (e) => { e.preventDefault(); await api.post("/assets", form); setOpen(false); setForm({ name: "", type: "LAPTOP", serialNumber: "" }); load(); };
  const assign = async (a, eid) => { await api.put(`/assets/${a.id}`, { assignedToId: eid }); load(); };
  if (!items) return <Loading />;

  return (
    <div>
      <PageHeader title="Assets" subtitle="Company asset inventory">
        {canManage && <button data-testid="add-asset" onClick={() => setOpen(true)} className="btn-accent"><Plus size={16} /> Add Asset</button>}
      </PageHeader>
      {items.length === 0 ? <EmptyState icon={Laptop} title="No assets" /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full" data-testid="asset-table">
            <thead><tr><th className="th">Asset</th><th className="th">Type</th><th className="th">Serial</th><th className="th">Book Value</th><th className="th">Status</th><th className="th">Assigned To</th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map((a) => (
                <tr key={a.id} data-testid={`asset-${a.id}`}>
                  <td className="td font-medium text-slate-800 dark:text-slate-100">{a.name}</td>
                  <td className="td"><Badge tone="info">{a.type}</Badge></td>
                  <td className="td font-mono text-xs">{a.serialNumber || "—"}</td>
                  <td className="td">{a.bookValue != null ? <div><span className="font-mono text-sm font-semibold text-slate-700 dark:text-slate-200">₹{a.bookValue.toLocaleString("en-IN")}</span><span className="ml-1 text-[10px] text-slate-400">(-{a.depreciationPct}%)</span></div> : <span className="text-xs text-slate-400">—</span>}</td>
                  <td className="td"><Badge value={a.status} /></td>
                  <td className="td">{canManage ? (
                    <select value={a.assignedToId || ""} onChange={(e) => assign(a, e.target.value)} data-testid={`assign-${a.id}`} className="rounded border border-slate-200 bg-transparent px-2 py-1 text-xs dark:border-slate-700">
                      <option value="">Unassigned</option>{emps.map((e) => <option key={e.id} value={e.id}>{e.fullName}</option>)}
                    </select>) : (a.assignedToName || "—")}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title="Add Asset"
        footer={<><button onClick={() => setOpen(false)} className="btn-outline">Cancel</button><button form="as-form" className="btn-accent">Add</button></>}>
        <form id="as-form" onSubmit={add} className="space-y-4">
          <div><label className="label">Name *</label><input required data-testid="as-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Type</label><select data-testid="as-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="input">{TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
            <div><label className="label">Serial Number</label><input data-testid="as-serial" value={form.serialNumber} onChange={(e) => setForm({ ...form, serialNumber: e.target.value })} className="input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="label">Purchase Cost (₹)</label><input type="number" data-testid="as-cost" value={form.purchaseCost || ""} onChange={(e) => setForm({ ...form, purchaseCost: e.target.value })} className="input" /></div>
              <div><label className="label">Useful Life (yrs)</label><input type="number" step="0.5" data-testid="as-life" value={form.usefulLifeYears || ""} onChange={(e) => setForm({ ...form, usefulLifeYears: e.target.value })} className="input" /></div>
            </div>
            <div><label className="label">Purchase Date</label><input type="date" data-testid="as-pdate" value={form.purchaseDate || ""} onChange={(e) => setForm({ ...form, purchaseDate: e.target.value })} className="input" /></div>
          </div>
        </form>
      </Modal>
    </div>
  );
}
